import { AxiosResponse } from 'axios';
import { api } from './config';
import {
  ShippingRecord, CreateShippingRecord, RepairRecord, CreateRepairRecord,
  PrepaidRecord, CreatePrepaidRecord, FuelReceipt
} from '../../types';

export const shippingApi = {
  getAll: (year?: number, month?: number): Promise<AxiosResponse<ShippingRecord[]>> => {
    if (year && month) {
      return api.get('/shipping', { params: { year, month } });
    }
    return api.get('/shipping');
  },
    
  getById: (id: number): Promise<AxiosResponse<ShippingRecord>> =>
    api.get(`/shipping/${id}`),
    
  create: (data: CreateShippingRecord): Promise<AxiosResponse<ShippingRecord>> =>
    api.post('/shipping/json', data),
    
  update: (id: number, data: CreateShippingRecord): Promise<AxiosResponse<ShippingRecord>> =>
    api.put(`/shipping/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shipping/${id}`),

  // Deleted records management
  getDeleted: (): Promise<AxiosResponse<ShippingRecord[]>> =>
    api.get('/shipping/deleted'),
    
  restore: (id: number): Promise<AxiosResponse<void>> =>
    api.post(`/shipping/restore/${id}`),

  // Add this method for delivery note sample
  getDeliveryNoteSample: (): Promise<AxiosResponse<any>> =>
    api.get('/shipping/delivery-note-sample'),

  // New linking/unlinking methods
  getAvailableFuelReceipts: (): Promise<AxiosResponse<FuelReceipt[]>> =>
    api.get('/shipping/available-fuel-receipts'),
    
  getAvailableRepairRecords: (): Promise<AxiosResponse<RepairRecord[]>> =>
    api.get('/shipping/available-repair-records'),
    
  linkFuelReceipt: (shippingRecordId: number, fuelReceiptId: number): Promise<AxiosResponse<void>> =>
    api.post(`/shipping/${shippingRecordId}/link-fuel-receipt/${fuelReceiptId}`),
    
  linkRepairRecord: (shippingRecordId: number, repairRecordId: number): Promise<AxiosResponse<void>> =>
    api.post(`/shipping/${shippingRecordId}/link-repair-record/${repairRecordId}`),
    
  unlinkFuelReceipt: (shippingRecordId: number, fuelReceiptId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shipping/${shippingRecordId}/unlink-fuel-receipt/${fuelReceiptId}`),
    
  unlinkRepairRecord: (shippingRecordId: number, repairRecordId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shipping/${shippingRecordId}/unlink-repair-record/${repairRecordId}`),

  getAvailablePrepaidRecords: (): Promise<AxiosResponse<PrepaidRecord[]>> =>
    api.get('/shipping/available-prepaid-records'),
    
  linkPrepaidRecord: (shippingRecordId: number, prepaidRecordId: number): Promise<AxiosResponse<void>> =>
    api.post(`/shipping/${shippingRecordId}/link-prepaid-record/${prepaidRecordId}`),
    
  unlinkPrepaidRecord: (shippingRecordId: number, prepaidRecordId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shipping/${shippingRecordId}/unlink-prepaid-record/${prepaidRecordId}`),
    
  getLinkedReceipts: (shippingRecordId: number): Promise<AxiosResponse<{fuelReceipts: FuelReceipt[], repairRecords: RepairRecord[], prepaidRecords: PrepaidRecord[]}>> =>
    api.get(`/shipping/${shippingRecordId}/linked-receipts`),

  // New: process delivery note image
  processDeliveryNote: (file: File): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/shipping/process-delivery-note', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Remove withCredentials: true since it's causing CORS issues
      // The JWT token in Authorization header is sufficient for authentication
      timeout: 600000, // 10 minutes timeout for AI processing
    });
  },

  // New: create shipping record with FormData (image/status)
  createWithImage: (data: FormData): Promise<AxiosResponse<any>> => {
    return api.post('/shipping', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Remove withCredentials: true since it's causing CORS issues
      // The JWT token in Authorization header is sufficient for authentication
    });
  },
};

// Repair API
export const repairApi = {
  getAll: (year?: number, month?: number): Promise<AxiosResponse<RepairRecord[]>> => {
    if (year && month) {
      return api.get('/repair', { params: { year, month } });
    }
    return api.get('/repair');
  },
    
  getById: (id: number): Promise<AxiosResponse<RepairRecord>> =>
    api.get(`/repair/${id}`),
    
  create: (data: CreateRepairRecord): Promise<AxiosResponse<RepairRecord>> =>
    api.post('/repair', data),
    
  update: (id: number, data: CreateRepairRecord): Promise<AxiosResponse<RepairRecord>> =>
    api.put(`/repair/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/repair/${id}`),

  // New methods for linking functionality
  unlink: (id: number): Promise<AxiosResponse<void>> =>
    api.post(`/repair/${id}/unlink`),
    
  getUnlinked: (): Promise<AxiosResponse<RepairRecord[]>> =>
    api.get('/repair/unlinked'),
};

// Fuel Receipt API
export const prepaidApi = {
  getAll: (): Promise<AxiosResponse<PrepaidRecord[]>> =>
    api.get('/prepaid'),
    
  getById: (id: number): Promise<AxiosResponse<PrepaidRecord>> =>
    api.get(`/prepaid/${id}`),
    
  create: (data: CreatePrepaidRecord): Promise<AxiosResponse<PrepaidRecord>> =>
    api.post('/prepaid', data),
    
  update: (id: number, data: CreatePrepaidRecord): Promise<AxiosResponse<void>> =>
    api.put(`/prepaid/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/prepaid/${id}`),

  // New methods for linking functionality
  unlink: (id: number): Promise<AxiosResponse<void>> =>
    api.post(`/prepaid/${id}/unlink`),
    
  getUnlinked: (): Promise<AxiosResponse<PrepaidRecord[]>> =>
    api.get('/prepaid/unlinked'),
};

// System API
